// Widget_RecentActivity — tasks 6.1 and 6.2 of the dashboard-redesign spec (Req 11).
//
// A read-only feed of the most recent up-to-25 events across the three
// activity sources the tenant cares about: financial records (bills and
// payments), statements, and reconciliation matches. Events are merged
// and time-sorted by `useRecentActivity(scope, limit)` (task 2.7); this
// widget is a passthrough renderer that owns four render responsibilities:
//
//   1. Frame chrome via <WidgetFrame> — loading / empty / ready / error
//      all share one bounding box so the surrounding grid never reflows
//      (Req 3.5).
//   2. Row layout — icon, event summary, building name, right-aligned
//      GBP amount (coloured per Owed/Owing semantics, Req 4), and a
//      relative timestamp ("just now", "5m ago", "2h ago", "3d ago").
//   3. Deep-links — each row is a Link to the source spec's detail page:
//        bill_created / payment_settled  → /dashboard/financial-records/{id}
//        statement_issued                → /dashboard/statements/{id}
//        reconciliation_matched          → /dashboard/banking
//      (payment_settled can additionally deep-link to a specific bank
//      transaction when the record carries both `bankAccountId` and
//      `bankTransactionId` — see `buildRowHref` below.)
//   4. Defence-in-depth — the whole tree is wrapped in <ErrorBoundary>
//      so a render exception in one row cannot blank the surrounding
//      dashboard (Req 3.6, Property P4).
//
// Task 6.2 extends the ready-state renderer with an inline partial-failure
// notice (Req 11.5): when `useRecentActivity` returns `ready` with a
// non-empty `partialFailures` array, an amber `role="alert"` panel is
// rendered ABOVE the feed listing each failed source and its `errorId`
// (monospace, matching `WidgetError`). Rows from the successful sources
// still render underneath. The notice hides when `partialFailures` is
// undefined or empty; the all-sources-failed case is handled upstream by
// the hook demoting to `status: "error"`, so this widget never has to.
//
// Standards: Tailwind + inline `style` for design-language tokens; no
// `dangerouslySetInnerHTML`; no `any`; strict-mode TypeScript.

import React from "react";
import { Link } from "react-router-dom";
import {
  ReceiptRefundIcon,
  EnvelopeIcon,
  CurrencyPoundIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { ErrorBoundary } from "../../../components/ui/ErrorBoundary";
import { WidgetFrame } from "../WidgetFrame";
import { WidgetError } from "../WidgetError";
import {
  useRecentActivity,
  type RecentActivitySource,
} from "../hooks/useRecentActivity";
import {
  COLOUR_NEUTRAL,
  formatMoneyGBP,
  getMonetaryColour,
} from "../formatMoney";
import type { MonetaryContext } from "../formatMoney";
import type { ActivityEvent, BuildingScope } from "../types";

/** Default number of events surfaced in the feed — Req 11.1 (25). */
const DEFAULT_LIMIT = 25;

/** Human-readable widget name — surfaced in the error card and section header. */
const WIDGET_NAME = "Recent Activity";

/** Empty-state copy — shown when the hook returns `empty`. */
const EMPTY_COPY = "No recent activity";

/**
 * Format an ISO 8601 timestamp as a compact relative time suitable for a
 * dashboard row. The output is deliberately short so the amount column
 * doesn't get pushed off the right edge of the tile:
 *
 *   < 60 seconds   → "just now"
 *   < 60 minutes   → "{n}m ago"
 *   < 24 hours     → "{n}h ago"
 *   otherwise      → "{n}d ago"
 *
 * Timestamps in the future clamp to "just now" so a small clock drift on
 * the API host doesn't produce a nonsensical "-1m ago". Unparsable input
 * returns "—" — the row still renders, it just doesn't display a time.
 *
 * `now` is injectable for deterministic tests; production callers use the
 * default (`Date.now()`).
 */
export function formatRelativeTime(
  iso: string,
  now: number = Date.now(),
): string {
  if (typeof iso !== "string" || iso.length === 0) return "—";
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return "—";
  const diffMs = now - parsed;
  // Clamp small negative diffs (clock skew) to "just now".
  if (diffMs < 60_000) return "just now";
  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Per-event-kind display metadata: which Heroicon renders in the leading
 * badge, the icon's foreground and background colour tokens (from
 * `design-language.md`), and the monetary context that drives the amount
 * colour (Owed = green, Owing = red — Req 4.4 / 4.5).
 */
interface KindDisplay {
  Icon: React.ForwardRefExoticComponent<
    React.PropsWithoutRef<React.SVGProps<SVGSVGElement>> & {
      title?: string;
      titleId?: string;
    } & React.RefAttributes<SVGSVGElement>
  >;
  /** Icon foreground colour (design-language semantic). */
  iconFg: string;
  /** Icon background tint. */
  iconBg: string;
  /** Owed / Owing context — drives amount colour via `getMonetaryColour`. */
  moneyContext: MonetaryContext;
  /** Label rendered before the contact/building name on the row. */
  summaryPrefix: string;
}

/**
 * Fixed mapping from `ActivityEvent.kind` to its display metadata. Kept
 * as a constant object rather than a switch so it is trivially testable
 * and remains a single source of truth for both icon and colour choices.
 */
const KIND_DISPLAY: Readonly<Record<ActivityEvent["kind"], KindDisplay>> = {
  // A bill was received — money is now owing by the tenant. Red per Req 4.5.
  bill_created: {
    Icon: ReceiptRefundIcon,
    iconFg: "#991b1b",
    iconBg: "#fee2e2",
    moneyContext: "owing",
    summaryPrefix: "Bill received from",
  },
  // A payment landed — money is owed to us and now realised. Green per Req 4.4.
  payment_settled: {
    Icon: CurrencyPoundIcon,
    iconFg: "#166534",
    iconBg: "#dcfce7",
    moneyContext: "owed",
    summaryPrefix: "Payment received from",
  },
  // A service-charge statement was issued to a resident. Green per Req 4.4.
  statement_issued: {
    Icon: EnvelopeIcon,
    iconFg: "#1e40af",
    iconBg: "#dbeafe",
    moneyContext: "owed",
    summaryPrefix: "Statement issued to",
  },
  // A bank transaction was matched against an outstanding item. Green.
  reconciliation_matched: {
    Icon: CheckCircleIcon,
    iconFg: "#166534",
    iconBg: "#dcfce7",
    moneyContext: "owed",
    summaryPrefix: "Reconciliation matched",
  },
};

/**
 * Read a string field from an opaque payload safely. The hook validates
 * only the common envelope (`id`, `ts`, `buildingId`, `buildingName`) and
 * passes everything else through as `unknown`-cast to the shared model
 * — so widget-level accessors defensively narrow before rendering.
 */
function readString(source: unknown, key: string): string | null {
  if (!source || typeof source !== "object") return null;
  const value = (source as Record<string, unknown>)[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}

/** Read an integer-pence money field, returning `null` when absent/invalid. */
function readPence(source: unknown, key: string): number | null {
  if (!source || typeof source !== "object") return null;
  const value = (source as Record<string, unknown>)[key];
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return value;
}

/**
 * Derive the row's headline summary. Falls back to a generic label when
 * the endpoint doesn't provide a contact/resident name so the row is
 * never blank.
 */
function readSummary(ev: ActivityEvent): string {
  const display = KIND_DISPLAY[ev.kind];
  switch (ev.kind) {
    case "bill_created": {
      const contact =
        readString(ev.record, "contactName") ??
        readString(ev.record, "supplierName");
      return contact ? `${display.summaryPrefix} ${contact}` : "Bill received";
    }
    case "payment_settled": {
      const contact =
        readString(ev.record, "contactName") ??
        readString(ev.record, "unitName");
      return contact
        ? `${display.summaryPrefix} ${contact}`
        : "Payment received";
    }
    case "statement_issued": {
      const resident =
        readString(ev.statement, "residentName") ??
        readString(ev.statement, "unitName");
      return resident
        ? `${display.summaryPrefix} ${resident}`
        : "Statement issued";
    }
    case "reconciliation_matched": {
      // Reconciliation events may carry a short description; render it
      // when present, otherwise fall back to the generic prefix.
      const description = readString(ev.match, "description");
      return description
        ? `${display.summaryPrefix}: ${description}`
        : display.summaryPrefix;
    }
  }
}

/**
 * Derive the row's amount (in integer pence). Uses the field name that
 * makes sense for each event kind and returns `null` when no amount is
 * available — the row then hides the amount slot rather than rendering
 * `£0.00`, which would visually assert "zero" when the actual answer is
 * "we didn't receive an amount from the endpoint".
 */
function readAmount(ev: ActivityEvent): number | null {
  switch (ev.kind) {
    case "bill_created":
      return readPence(ev.record, "amount");
    case "payment_settled":
      // Prefer `amountPaid` (what settled), fall back to `amount` (bill total).
      return (
        readPence(ev.record, "amountPaid") ?? readPence(ev.record, "amount")
      );
    case "statement_issued":
      return readPence(ev.statement, "totalAmount");
    case "reconciliation_matched":
      return readPence(ev.match, "amount");
  }
}

/**
 * Read the underlying record's `id` for building deep-link URLs. The
 * event's own envelope carries a matching `id` on the source object.
 */
function readRecordId(ev: ActivityEvent): string | null {
  switch (ev.kind) {
    case "bill_created":
    case "payment_settled":
      return readString(ev.record, "id");
    case "statement_issued":
      return readString(ev.statement, "id");
    case "reconciliation_matched":
      return readString(ev.match, "id");
  }
}

/**
 * Deep-link target per event kind:
 *
 *   bill_created           → /dashboard/financial-records/{id}
 *   payment_settled        → /dashboard/banking/{acct}/transactions/{tx}/edit
 *                            if `bankAccountId` AND `bankTransactionId` are
 *                            both present on the record; otherwise falls
 *                            back to /dashboard/financial-records/{id} so
 *                            the row remains reachable.
 *   statement_issued       → /dashboard/statements/{id}
 *   reconciliation_matched → /dashboard/banking (per task spec — the
 *                            reconciliation surface doesn't yet have a
 *                            dedicated route).
 */
function buildRowHref(ev: ActivityEvent): string {
  const id = readRecordId(ev);
  switch (ev.kind) {
    case "bill_created":
      return id
        ? `/dashboard/financial-records/${encodeURIComponent(id)}`
        : "/dashboard/financial-records";
    case "payment_settled": {
      const acct = readString(ev.record, "bankAccountId");
      const tx = readString(ev.record, "bankTransactionId");
      if (acct && tx) {
        return `/dashboard/banking/${encodeURIComponent(
          acct,
        )}/transactions/${encodeURIComponent(tx)}/edit`;
      }
      return id
        ? `/dashboard/financial-records/${encodeURIComponent(id)}`
        : "/dashboard/banking";
    }
    case "statement_issued":
      return id
        ? `/dashboard/statements/${encodeURIComponent(id)}`
        : "/dashboard/statements";
    case "reconciliation_matched":
      return "/dashboard/banking";
  }
}

/**
 * Sort-stable key for a rendered row. Prefers the underlying record id
 * (so a single event has one DOM key across re-renders) and falls back
 * to a compound of `ts` + `kind` when the id is missing.
 */
function rowKey(ev: ActivityEvent): string {
  const id = readRecordId(ev);
  return id ? `${ev.kind}:${id}` : `${ev.kind}:${ev.ts}`;
}

/**
 * The four visual pieces of a single row: icon badge, summary + building,
 * amount, timestamp. Split out for readability — the widget's ready-state
 * body maps events to `<ActivityRow>`s.
 */
function ActivityRow({
  event,
  now,
}: {
  event: ActivityEvent;
  /** Reference `now` for the relative-time formatter — injectable for tests. */
  now?: number;
}): JSX.Element {
  const display = KIND_DISPLAY[event.kind];
  const { Icon } = display;
  const summary = readSummary(event);
  const amountPence = readAmount(event);
  const relative = formatRelativeTime(event.ts, now ?? Date.now());
  const href = buildRowHref(event);
  const amountColour =
    amountPence === null
      ? COLOUR_NEUTRAL
      : getMonetaryColour(amountPence, display.moneyContext);

  return (
    <Link
      to={href}
      data-testid={`activity-row-${event.kind}-${
        readRecordId(event) ?? event.ts
      }`}
      data-kind={event.kind}
      className="flex items-start gap-3 py-2 px-1 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      <span
        aria-hidden="true"
        data-testid={`activity-row-icon-${event.kind}`}
        className="flex-shrink-0 inline-flex items-center justify-center rounded-full"
        style={{
          width: 32,
          height: 32,
          backgroundColor: display.iconBg,
          color: display.iconFg,
        }}
      >
        <Icon width={20} height={20} strokeWidth={2} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <div
            data-testid="activity-row-summary"
            className="text-sm font-medium truncate"
            style={{ color: "#111827" }}
          >
            {summary}
          </div>
          {amountPence !== null && (
            <div
              data-testid="activity-row-amount"
              className="text-sm font-semibold tabular-nums flex-shrink-0"
              style={{ color: amountColour }}
            >
              {formatMoneyGBP(amountPence)}
            </div>
          )}
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <div
            data-testid="activity-row-building"
            className="text-xs truncate"
            style={{ color: "#6b7280" }}
          >
            {event.buildingName || "—"}
          </div>
          <div
            data-testid="activity-row-timestamp"
            className="text-xs whitespace-nowrap flex items-center gap-1 flex-shrink-0"
            style={{ color: "#6b7280" }}
          >
            <span>{relative}</span>
            <span aria-hidden="true">↗</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

/** One entry in the hook's ready-state `partialFailures` array. */
interface PartialFailureEntry {
  source: RecentActivitySource;
  errorId: string;
}

/**
 * Amber, `role="alert"` inline notice rendered above the feed when the
 * hook stayed in `ready` but 1–2 of the 3 sources rejected (Req 11.5).
 *
 * Design-language "attention" tokens: `#fef3c7` background, `#92400e`
 * body text — the same amber pair used for the supplier badge in
 * `design-language.md`. Copy is fixed ("Some sources didn't load. Others
 * are showing below.") followed by a bullet list of the failed source
 * names and their `errorId`s in monospace so a treasurer can quote either
 * to support (matches the pattern in `WidgetError`).
 *
 * Never rendered when `failures` is empty — the caller filters before
 * invoking so this component doesn't have to defend against it, but a
 * defensive `null` return keeps the widget bulletproof if callers
 * regress.
 */
function PartialFailuresNotice({
  failures,
}: {
  failures: PartialFailureEntry[];
}): JSX.Element | null {
  if (failures.length === 0) return null;
  return (
    <div
      role="alert"
      data-testid="widget-recent-activity-partial-notice"
      className="mb-3 flex items-start gap-2 rounded-lg border px-3 py-2"
      style={{
        backgroundColor: "#fef3c7",
        borderColor: "#fde68a",
        color: "#92400e",
      }}
    >
      <ExclamationTriangleIcon
        aria-hidden="true"
        width={18}
        height={18}
        strokeWidth={2}
        className="flex-shrink-0 mt-0.5"
        style={{ color: "#92400e" }}
      />
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium">
          Some sources didn&apos;t load. Others are showing below.
        </div>
        <ul className="mt-1 flex flex-col gap-0.5 text-xs">
          {failures.map((f) => (
            <li
              key={`${f.source}:${f.errorId}`}
              data-testid={`widget-recent-activity-partial-source-${f.source}`}
              className="flex flex-wrap items-baseline gap-x-1"
            >
              <span aria-hidden="true">•</span>
              <span className="font-medium">{f.source}</span>
              <span>
                (Error id:{" "}
                <span
                  data-testid={`widget-recent-activity-partial-errorid-${f.source}`}
                  className="font-mono text-xs"
                  style={{ fontFamily: "monospace" }}
                >
                  {f.errorId}
                </span>
                )
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/**
 * Ready-state body. Renders up to `limit` rows in the order returned by
 * the hook (which is already time-sorted DESC per Req 11 / P6). No
 * client-side re-sort — the hook is the single source of truth for order.
 *
 * When the hook exposes a non-empty `partialFailures` array (1–2 of the
 * 3 sources rejected), a `<PartialFailuresNotice>` renders ABOVE the
 * list. Successful rows continue to render below. Hidden when
 * `partialFailures` is undefined or empty (Req 11.5).
 */
function ActivityReady({
  events,
  partialFailures,
  now,
}: {
  events: ActivityEvent[];
  /** Non-empty when 1–2 sources failed but at least 1 succeeded. */
  partialFailures?: PartialFailureEntry[];
  now?: number;
}): JSX.Element {
  const failures =
    partialFailures && partialFailures.length > 0 ? partialFailures : null;
  return (
    <div className="flex flex-col h-full">
      {failures !== null && <PartialFailuresNotice failures={failures} />}
      <ul
        className="flex flex-col divide-y"
        style={{ borderColor: "#f3f4f6" }}
        data-testid="widget-recent-activity-list"
      >
        {events.map((event) => (
          <li key={rowKey(event)}>
            <ActivityRow event={event} now={now} />
          </li>
        ))}
      </ul>
    </div>
  );
}

export interface WidgetRecentActivityProps {
  scope: BuildingScope;
  /** Max events to render. Defaults to 25 per Req 11.1. */
  limit?: number;
  /**
   * Optional reference `now` (epoch ms) for the relative timestamp
   * formatter. Production callers omit this and let each row read
   * `Date.now()` at render time. Test suites pass a fixed value so the
   * assertions on "2h ago" / "3d ago" don't drift with wall-clock time.
   */
  now?: number;
}

/**
 * Widget chrome — a white rounded card with a header. Kept as a local
 * component so every render state (skeleton / empty / ready / error)
 * shares the same bounding box and the surrounding grid doesn't reflow
 * between transitions (Req 3.5).
 */
function TileFrame({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div
      data-testid="widget-recent-activity"
      className="bg-white rounded-xl border h-full flex flex-col p-4"
      style={{ borderColor: "#e5e7eb" }}
    >
      <div
        className="text-sm font-semibold flex items-center justify-between"
        style={{ color: "#111827" }}
      >
        <span>{WIDGET_NAME}</span>
      </div>
      <div className="mt-2 border-t" style={{ borderColor: "#e5e7eb" }} />
      <div className="flex-1 min-h-0 mt-2">{children}</div>
    </div>
  );
}

/**
 * Inner component that consumes the hook. Kept as a separate function so
 * the hook call sits INSIDE the <ErrorBoundary> — a hook that throws
 * during render is then caught by the boundary instead of propagating
 * into the surrounding dashboard. Mirrors the pattern used by
 * `Widget_UpcomingMeetings`.
 */
function WidgetRecentActivityInner({
  scope,
  limit,
  now,
}: {
  scope: BuildingScope;
  limit: number;
  now?: number;
}): JSX.Element {
  const result = useRecentActivity(scope, limit);

  // The hook's extended `RecentActivityReady` variant is structurally a
  // superset of `WidgetResult<ActivityEvent[]>`'s ready variant, so it
  // flows through <WidgetFrame> unchanged. When the hook returned ready
  // with a non-empty `partialFailures` array we thread it into
  // <ActivityReady> so the inline notice can render above the feed
  // (Req 11.5). `"partialFailures" in result` narrows the union safely.
  const partialFailures: PartialFailureEntry[] | undefined =
    result.status === "ready" && "partialFailures" in result
      ? result.partialFailures
      : undefined;

  return (
    <WidgetFrame
      result={result}
      name={WIDGET_NAME}
      emptyCopy={EMPTY_COPY}
      skeletonHeight={220}
    >
      {result.status === "ready" && (
        <ActivityReady
          events={result.data}
          partialFailures={partialFailures}
          now={now}
        />
      )}
    </WidgetFrame>
  );
}

/**
 * Public widget. Wraps the hook-consuming inner tree in an
 * <ErrorBoundary> for defence-in-depth so a render-time exception in
 * this widget's subtree cannot blank the surrounding dashboard
 * (Requirement 3.6, Property P4).
 */
export function Widget_RecentActivity({
  scope,
  limit = DEFAULT_LIMIT,
  now,
}: WidgetRecentActivityProps): JSX.Element {
  return (
    <ErrorBoundary
      fallback={
        <TileFrame>
          <WidgetError name={WIDGET_NAME} errorId="error-boundary" />
        </TileFrame>
      }
    >
      <TileFrame>
        <WidgetRecentActivityInner scope={scope} limit={limit} now={now} />
      </TileFrame>
    </ErrorBoundary>
  );
}

export default Widget_RecentActivity;
